import { useMemo, useState } from 'react';

import { Check, ChevronDown, Copy, ExternalLink } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import { classifyValue, humanizeKey } from './formatField';

import type { FieldValue } from './formatField';

const plainText = (value: FieldValue): string => {
  if (value.kind === 'empty') return '';
  if (value.kind === 'list') return value.items.map(plainText).join(', ');
  if (value.kind === 'group') {
    return value.entries.map(([k, v]) => `${k} ${plainText(v)}`).join(' ');
  }
  return value.text;
};

/** Keeps entries whose label or value contains the filter; empty ones only when shown. */
const visible = (entries: [string, FieldValue][], filter: string, showEmpty: boolean) =>
  entries.filter(([key, value]) => {
    if (value.kind === 'empty') return showEmpty && (!filter || key.toLowerCase().includes(filter));
    if (!filter) return true;
    return `${humanizeKey(key)} ${plainText(value)}`.toLowerCase().includes(filter);
  });

const CopyButton = ({ text, label }: { text: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable or denied: nothing to show.
    }
  };
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => void copy()}
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring cursor-pointer rounded p-1 opacity-100 transition-opacity duration-150 focus-visible:ring-2 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
};

const Value = ({ value }: { value: FieldValue }) => {
  const [expanded, setExpanded] = useState(false);
  switch (value.kind) {
    case 'empty':
      return <span className="text-muted-foreground">—</span>;
    case 'boolean':
      return <Badge variant={value.text === 'Yes' ? 'default' : 'secondary'}>{value.text}</Badge>;
    case 'image':
      return (
        <a
          href={value.href}
          target="_blank"
          rel="noreferrer"
          className="inline-block cursor-pointer"
        >
          <img
            src={value.text}
            alt="Listing image"
            loading="lazy"
            className="h-16 w-24 rounded border object-cover"
          />
        </a>
      );
    case 'url':
      return (
        <a
          href={value.href}
          target="_blank"
          rel="noreferrer"
          className="text-primary inline-flex cursor-pointer items-center gap-1 break-all hover:underline"
        >
          {value.text}
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
      );
    case 'list':
      return value.items.every((i) => i.kind !== 'group' && i.kind !== 'list') ? (
        <div className="flex flex-wrap gap-1">
          {value.items.map((item, i) =>
            item.kind === 'image' ? (
              <Value key={i} value={item} />
            ) : (
              <Badge key={i} variant="outline" className="font-normal">
                {plainText(item) || '—'}
              </Badge>
            ),
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {value.items.map((item, i) => (
            <div key={i} className="rounded-md border p-2">
              <Value value={item} />
            </div>
          ))}
        </div>
      );
    case 'group':
      return <FieldGrid entries={value.entries} filter="" showEmpty={false} />;
    default: {
      const long = value.text.length > 240;
      return (
        <span className="break-words whitespace-pre-wrap">
          {long && !expanded ? `${value.text.slice(0, 240)}…` : value.text}
          {long && (
            <button
              type="button"
              className="text-primary ml-1 cursor-pointer text-xs hover:underline"
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? 'less' : 'more'}
            </button>
          )}
        </span>
      );
    }
  }
};

const FieldGrid = ({
  entries,
  filter,
  showEmpty,
}: {
  entries: [string, FieldValue][];
  filter: string;
  showEmpty: boolean;
}) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[minmax(140px,220px)_1fr]">
    {visible(entries, filter, showEmpty).map(([key, value]) => (
      <div key={key} className="group contents">
        <dt className="text-muted-foreground pt-0.5 text-sm">{humanizeKey(key)}</dt>
        <dd className="flex min-w-0 items-start gap-1 text-sm">
          <div className="min-w-0 flex-1">
            <Value value={value} />
          </div>
          {value.kind !== 'empty' && value.kind !== 'group' && value.kind !== 'list' && (
            <CopyButton text={value.href ?? value.text} label={humanizeKey(key)} />
          )}
        </dd>
      </div>
    ))}
  </dl>
);

export const DynamicFields = ({ data }: { data: Record<string, unknown> }) => {
  const [filter, setFilter] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);
  const needle = filter.trim().toLowerCase();

  // Top-level objects become sections; loose top-level values go in "General".
  const sections = useMemo(() => {
    const general: [string, FieldValue][] = [];
    const groups: { key: string; entries: [string, FieldValue][] }[] = [];
    for (const [key, raw] of Object.entries(data)) {
      const value = classifyValue(key, raw);
      if (value.kind === 'group') groups.push({ key, entries: value.entries });
      else general.push([key, value]);
    }
    return [...(general.length ? [{ key: '__general', entries: general }] : []), ...groups];
  }, [data]);

  const shown = sections
    .map((s) => ({ ...s, rows: visible(s.entries, needle, showEmpty) }))
    .filter((s) => s.rows.length > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <Input
          aria-label="Filter fields"
          placeholder="Filter fields…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={showEmpty}
            onCheckedChange={setShowEmpty}
            aria-label="Show empty fields"
          />
          Show empty fields
        </label>
      </div>
      {shown.length === 0 && (
        <p className="text-muted-foreground text-sm">No fields match &quot;{filter}&quot;.</p>
      )}
      {shown.map((section) => (
        <Collapsible key={section.key} defaultOpen className="rounded-lg border">
          <CollapsibleTrigger className="hover:bg-muted/50 focus-visible:ring-ring flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left text-sm font-medium transition-colors focus-visible:ring-2">
            {humanizeKey(section.key)}
            <span className="text-muted-foreground flex items-center gap-2 text-xs">
              {section.rows.length}
              <ChevronDown className="h-4 w-4" aria-hidden />
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t px-4 py-3">
            <FieldGrid entries={section.rows} filter="" showEmpty={showEmpty} />
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
};
